import { Button } from "@mixitone/components";
import {
  Account,
  AccountUser,
  Club,
  ClubDocument,
  ClubPlayer,
  ClubPlayerAcceptedDocument,
  ClubPlayerToken,
  Night,
  Note,
  Player,
  Token,
  acceptanceCoversDocument,
  acceptanceMatchesSeries,
  acceptanceSameVersion,
  documentChasesCurrentMembers,
} from "@mixitone/models";
import { List, ValidationError } from "@mixitone/oom";
import { formatDate, isPresent, withStateToggle } from "@mixitone/util";
import { captureException } from "@mixitone/telemetry";
import routeService from "services/RouteService";
import { messagesService } from "services";
import { FormController } from "./FormController";
import type { FormFieldConfig } from "./FormFieldsDefinition";
import GroupSelectField from "./custom-fields/GroupSelectField";
import NfcTokenField from "./custom-fields/NfcTokenField";
import PlayerSearchField from "./custom-fields/PlayerSearchField";

interface Props {
  clubId: string;
  tokenId?: string;
  sessionId?: string;
  search?: boolean;
  autoFocus?: boolean;
  canDelete?: boolean;
  canCancel?: boolean;
  onSubmit?: (player: ClubPlayer) => void;
}

class ClubPlayerFormController extends FormController<
  ClubPlayer,
  { name?: string; email?: string; phone?: string; token?: string },
  {
    sessionCount: number;
    lastSession: string | null | Date;
    club: Club;
    clubDocuments: List<ClubDocument>;
    clubPlayers: List<ClubPlayer>;
    search: boolean;
    searchTerm: string;
    token?: string;
    tokens?: ClubPlayerToken[];
    showDeleteTokenModal: string | null;
    memberNote: Note | null;
    memberNoteText: string;
    notesUpdating: boolean;
    membershipUpdating: boolean;
    showEndMembershipModal: boolean;
    membershipRequirements: { message: string; field: string }[];
  },
  Props
> {
  private _fieldsCacheKey = "";
  private _fieldsCache: Record<string, FormFieldConfig> | null = null;
  private _fieldsByGroupCache = new Map<string, FormFieldConfig[]>();

  override get initialState() {
    return {
      ...super.initialState,
      memberNote: null,
      memberNoteText: "",
      notesUpdating: false,
      membershipUpdating: false,
      showEndMembershipModal: false,
      membershipRequirements: [],
    };
  }

  override findModel(id: string): Promise<ClubPlayer> {
    return ClubPlayer.query().preload("group", "accepted_documents", "memberships", "notes").find(id);
  }
  override async buildRecord(): Promise<ClubPlayer> {
    await this.loadClub();

    return new ClubPlayer({
      active: true,
      club_id: this.props.clubId,
      name: this.state.attributes.name,
      email: this.state.attributes.email,
      phone: this.state.attributes.phone,
    });
  }
  override async saveRecord(record: ClubPlayer): Promise<void> {
    record.club_id = this.props.clubId;
    await record.save();

    if (this.props.tokenId) {
      const cpt = new ClubPlayerToken({
        club_player_id: record.id,
        token_id: this.props.tokenId,
      });
      await cpt.save();
    }

    await record.accepted_documents.save();

    if (this.props.sessionId) {
      const session = await Night.query().findBy("id", this.props.sessionId);
      if (session) {
        await session.addClubPlayer(record);
      }
    }

    if (this.props.onSubmit) {
      this.props.onSubmit(record);
    }
  }
  async loadClub() {
    if (this.state.club?.id === this.props.clubId) {
      console.log("Club already loaded", this.props.clubId);
      return;
    }

    this.state.club = await Club.query().find(this.props.clubId);
    await this.state.club.groups.reload();
    const documents = await this.state.club.club_documents.query.all();
    this.state.clubDocuments = new List(
      ...ClubDocument.onlyLastVersion(documents.filter((doc) => !doc.is_deleted)).filter((doc) =>
        documentChasesCurrentMembers(doc),
      ),
    );
  }

  override async onLoad() {
    await this.loadClub();

    if (this.props.search) {
      await this.state.club.club_players.load();
      this.state.clubPlayers = new List(...this.state.club.club_players);
    }

    if (this.state.newRecord) {
      if (this.props.tokenId) {
        const token = await Token.query().find(this.props.tokenId);
        this.state.token = token?.token_value;

        const otherClubPlayer = await ClubPlayer.query()
          .joins(ClubPlayerToken.query().eq("token_id", this.props.tokenId))
          .neq("club_id", this.props.clubId)
          .orderBy("created_at", "desc")
          .first();

        if (otherClubPlayer) {
          // Pre-fill the form with the other club player's details
          (["name", "email", "phone"] as const).forEach((key) => {
            if (!this.state.attributes[key]) {
              this.state.attributes[key] = otherClubPlayer[key];
              this.state.record.writeAttribute(key, otherClubPlayer[key]);
            }
          });
          this.state.record.player_profile_id = otherClubPlayer.player_profile_id;
        }
      }
    }

    (["email", "phone"] as const).forEach((key) => {
      if (isPresent(this.state.attributes[key])) {
        this.state.record.writeAttribute(key, this.state.attributes[key]);
      }
    });

    if (this.state.record.persisted) {
      const tokens = await ClubPlayerToken.query()
        .eq("club_player_id", this.state.record.id)
        .preload("token")
        .all();
      const clubPlayerId = this.state.record.id!;
      const nightsForClubPlayer = () =>
        Night.query().joins(Player.query().eq("club_player_id", clubPlayerId));
      const [sessionCount, lastSessions] = await Promise.all([
        nightsForClubPlayer().count(),
        nightsForClubPlayer().orderBy("date", "desc").limit(1).pluck("date"),
      ]);
      const lastSession = lastSessions?.[0];
      this.setState({ sessionCount, lastSession, tokens });
      await this.reloadMemberNote();
    }
  }

  async reloadMemberNote() {
    await this.state.record.notes.reload();
    const memberNotes = [...this.state.record.notes]
      .filter((note) => !note.night_id && !note.is_deleted)
      .toSorted((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime());
    const note = memberNotes[0] ?? null;
    this.state.memberNote = note;
    this.state.memberNoteText = note?.note ?? "";
  }

  async actionSaveMemberNote(text: string) {
    const trimmed = text.trim();
    const currentText = this.state.memberNoteText.trim();

    if (trimmed === currentText) {
      return;
    }

    this.state.notesUpdating = true;
    try {
      if (!trimmed) {
        if (this.state.memberNote?.persisted) {
          await this.state.memberNote.destroy();
        }
        await this.destroyExtraMemberNotes(null);
        this.state.memberNote = null;
        this.state.memberNoteText = "";
        await this.reloadMemberNote();
        return;
      }

      let note = this.state.memberNote;
      if (note?.persisted) {
        note.note = trimmed;
        await note.save();
      } else {
        note = new Note({
          account_user_id: AccountUser.current?.id,
          club_player_id: this.state.record.id,
          note: trimmed,
        });
        await note.save();
        this.state.memberNote = note;
      }

      await this.destroyExtraMemberNotes(note);
      this.state.memberNoteText = trimmed;
      await this.reloadMemberNote();
    } finally {
      this.state.notesUpdating = false;
    }
  }

  async destroyExtraMemberNotes(keepNote: Note | null) {
    const memberNotes = [...this.state.record.notes].filter((note) => !note.night_id && !note.is_deleted);
    for (const note of memberNotes) {
      if (note.id !== keepNote?.id) {
        await note.destroy();
      }
    }
  }

  fieldsForGroup(group: "profile" | "membership"): FormFieldConfig[] {
    const cacheKey = `${this.fieldsCacheKey()}:${group}`;
    const cached = this._fieldsByGroupCache.get(cacheKey);
    if (cached) return cached;

    const result = Object.values(this.fields()).filter((field) => field.group === group);
    this._fieldsByGroupCache.set(cacheKey, result);
    return result;
  }

  private fieldsCacheKey(): string {
    const { record, club, clubDocuments, newRecord, token } = this.state;
    if (!record || !club) return "";

    const group = this.group;
    const acceptedKey = [...record.accepted_documents]
      .map((ad) => `${ad.document_name}:${ad.version}:${ad.is_deleted}`)
      .join(",");

    return [
      this.props.search,
      newRecord,
      token ?? "",
      record.isCurrentMember,
      record.group_id ?? "",
      clubDocuments.map((d) => `${d.id}:${d.version}`).join(","),
      acceptedKey,
      group?.graduation_enabled,
      Boolean(Account.current?.hasFeature("group_levels")),
      Boolean(Account.current?.hasFeature("member_players")),
      Boolean(club.getPreference("hasMembers", false)),
      club.getMemberRequirement("email", false),
      club.getMemberRequirement("phone", false),
      club.memberTypes.length,
    ].join("|");
  }

  get group() {
    return (
      this.state.club.groups.find((group) => group.id === this.state.record.group_id) ||
      this.state.club.groups[0]
    );
  }

  override get canDelete(): boolean {
    if (this.state.newRecord) return false;
    if (this.props.search) return false;
    if (this.props.canDelete === false) return false;
    if (!this.state.club) return false;
    return this.state.club.hasManagePermission();
  }

  async actionSearchSelect(player: ClubPlayer) {
    if (!player || !player.persisted) {
      return;
    }

    const { record } = this.state;
    (["email", "phone"] as const).forEach((key) => {
      if (record.changedAttributes.has(key) && isPresent(record.readAttribute(key))) {
        player.writeAttribute(key, record.readAttribute(key));
      }
    });

    this.state.record = player;
    this.state.newRecord = false;
  }

  async actionDeleteToken(clubPlayerTokenId: string) {
    this.state.showDeleteTokenModal = clubPlayerTokenId;
  }

  actionCancelDeleteToken() {
    this.state.showDeleteTokenModal = null;
  }

  async actionConfirmDeleteToken() {
    const clubPlayerTokenId = this.state.showDeleteTokenModal;
    if (!clubPlayerTokenId) return;

    const token = this.state.tokens?.find((t) => t.id === clubPlayerTokenId);
    if (token) {
      await token.destroy();
      this.state.tokens = this.state.tokens?.filter((t) => t.id !== clubPlayerTokenId);
    }
    this.state.showDeleteTokenModal = null;
  }

  get membershipsEnabled(): boolean {
    return (
      Boolean(Account.current?.hasFeature("member_players")) &&
      Boolean(this.state.club?.getPreference("hasMembers", false))
    );
  }

  get membershipPeriodsForDisplay() {
    const { record } = this.state;
    if (!record.memberships.loaded) return [];
    return [...record.memberships]
      .filter((membership) => !membership.is_deleted)
      .toSorted((a, b) => {
        const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
        const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
        return bTime - aTime;
      });
  }

  async actionLoadMembershipRequirements() {
    if (!this.membershipsEnabled || this.state.newRecord) return;
    const [, reasons] = await this.state.record.canBeMember();
    this.state.membershipRequirements = reasons;
  }

  @withStateToggle("membershipUpdating")
  async actionStartMembership() {
    const { record } = this.state;
    try {
      const [canStart, reasons] = await record.canBeMember();
      this.state.membershipRequirements = reasons;
      if (!canStart) {
        messagesService.addMessage("Complete the required fields before starting membership", "error");
        return;
      }

      await record.startMembership();
      messagesService.addMessage("Membership started", "success");
    } catch (err) {
      captureException(err);
      if (err instanceof ValidationError) {
        const message = Object.values(err.errors).join(", ");
        messagesService.addMessage(message || "Could not start membership", "error");
      } else {
        messagesService.addMessage("Could not start membership", "error");
      }
    }
  }

  actionEndMembership() {
    this.state.showEndMembershipModal = true;
  }

  actionCancelEndMembership() {
    this.state.showEndMembershipModal = false;
  }

  @withStateToggle("membershipUpdating")
  async actionConfirmEndMembership() {
    const { record } = this.state;
    try {
      await record.endMembership();
      this.state.showEndMembershipModal = false;
      messagesService.addMessage("Membership ended", "success");
    } catch (err) {
      captureException(err);
      if (err instanceof ValidationError) {
        const message = Object.values(err.errors).join(", ");
        messagesService.addMessage(message || "Could not end membership", "error");
      } else {
        messagesService.addMessage("Could not end membership", "error");
      }
    }
  }

  fields() {
    const cacheKey = this.fieldsCacheKey();
    if (cacheKey && this._fieldsCacheKey === cacheKey && this._fieldsCache) {
      return this._fieldsCache;
    }

    const fields = this.buildFields();
    this._fieldsCacheKey = cacheKey;
    this._fieldsCache = fields;
    this._fieldsByGroupCache.clear();
    return fields;
  }

  private buildFields() {
    const { record, club } = this.state;
    if (!record || !club) {
      return {} satisfies Record<string, FormFieldConfig>;
    }

    const isMember = Account.current?.hasFeature("member_players") && record.isCurrentMember;

    const nameField: FormFieldConfig = this.props.search
      ? {
          name: "name",
          type: "custom",
          label: "Name",
          required: true,
          component: PlayerSearchField,
          group: "profile",
        }
      : {
          name: "name",
          type: "text",
          label: "Name",
          required: true,
          group: "profile",
        };

    const fields: Record<string, FormFieldConfig> = {
      name: nameField,
    };

    Object.assign(fields, {
      nfc_token: {
        name: "nfc_token",
        type: "custom",
        label: "NFC token",
        component: NfcTokenField,
        condition: () => !this.state.newRecord || !!this.state.token,
        group: "profile",
      },
      active: {
        name: "active",
        type: "checkbox",
        label: "Active",
        condition: () => !this.state.newRecord,
        group: "profile",
      },
      group_id: {
        name: "group_id",
        type: "custom",
        label: "Group",
        component: GroupSelectField,
        group: "profile",
      },
      graduation_level: {
        name: "graduation_level",
        type: "select",
        label: "Level",
        hidePlaceholderOption: true,
        options: [
          { value: 1, label: "1" },
          { value: 2, label: "2" },
          { value: 3, label: "3" },
        ],
        condition: () =>
          Boolean(this.group?.graduation_enabled && Account.current?.hasFeature("group_levels")),
        group: "profile",
        value: () => String(record.graduation_level ?? 1),
        onChange: (raw: string) => {
          record.graduation_level = Number(raw);
        },
      },
      email: {
        name: "email",
        type: "text",
        inputType: "email",
        label: "Email",
        group: "profile",
        required: isMember && club.getMemberRequirement("email", false),
      },
      phone: {
        name: "phone",
        type: "text",
        inputType: "tel",
        label: "Phone",
        group: "profile",
        required: isMember && club.getMemberRequirement("phone", false),
      },
    });

    if (isMember && this.state.clubDocuments.length > 0) {
      for (const doc of this.state.clubDocuments) {
        if (!doc.name) continue;
        const prior = record.accepted_documents
          .filter((ad) => acceptanceMatchesSeries(ad, doc))
          .toSorted((a, b) => (b.version ?? 0) - (a.version ?? 0))[0];

        fields[`document_${doc.name}`] = {
          name: `document_${doc.name}`,
          type: "checkbox",
          group: "membership",
          label: `Accept ${doc.name} v${doc.version}`,
          description: (
            <span className="flex justify-between">
              <Button
                kind="link"
                onClick={(e) => {
                  e.preventDefault();
                  routeService.openModal("/clubs/:clubId/documents/:documentId/show", {
                    params: { clubId: this.props.clubId, documentId: doc.id! },
                  });
                }}
              >
                Read document
              </Button>
              {prior ? (
                <span>
                  Accepted v{prior.version} on {formatDate(prior.accepted_at)}
                </span>
              ) : (
                <span>Not accepted</span>
              )}
            </span>
          ),
          required: true,
          value: () => record.accepted_documents.some((ad) => acceptanceCoversDocument(ad, doc)),
          onChange: (value: boolean) => {
            const existing =
              record.accepted_documents.find((ad) => !ad.is_deleted && acceptanceSameVersion(ad, doc)) ??
              record.accepted_documents.find((ad) => acceptanceSameVersion(ad, doc));
            if (value) {
              if (existing && !existing.is_deleted) return;
              if (existing) {
                existing.is_deleted = false;
                existing.accepted_at = new Date();
                existing.recorded_by_user_id = AccountUser.current?.user_id;
                return;
              }
              record.accepted_documents.add(
                new ClubPlayerAcceptedDocument({
                  document_name: doc.name!,
                  series_id: doc.series_id,
                  club_document_id: doc.id,
                  version: doc.version!,
                  source: "admin",
                  recorded_by_user_id: AccountUser.current?.user_id,
                  accepted_at: new Date().toISOString(),
                }),
              );
            } else if (existing?.persisted) {
              existing.is_deleted = true;
            } else if (existing) {
              const index = record.accepted_documents.indexOf(existing);
              record.accepted_documents.splice(index, 1);
            }
          },
        };
      }
    }

    return fields satisfies Record<string, FormFieldConfig>;
  }
}

export default ClubPlayerFormController;
